import { Node, appendChild, createElement, fragment, text } from "./tree";
import { Block, CompositeDecorator } from "./types";

interface Decoration {
    begin: number;
    end: number;
    match: RegExpMatchArray;
    decorator: CompositeDecorator;
}

/**
 * Find all decorations in text, preventing overlaps.
 * First decorator to match a position wins.
 */
function getDecorations(
    decorators: CompositeDecorator[],
    textContent: string,
): Decoration[] {
    const occupied = new Map<number, boolean>();
    const decorations: Decoration[] = [];

    for (const decorator of decorators) {
        // Convert string patterns to RegExp
        const regex =
            typeof decorator.strategy === "string"
                ? new RegExp(decorator.strategy, "g")
                : decorator.strategy;

        // Reset lastIndex for global regexes
        regex.lastIndex = 0;

        for (const match of textContent.matchAll(regex)) {
            const begin = match.index;
            const end = begin + match[0].length;

            // Check if any character in range is already occupied
            let isOccupied = false;
            for (let i = begin; i < end; i++) {
                if (occupied.get(i)) {
                    isOccupied = true;
                    break;
                }
            }

            if (!isOccupied) {
                // Mark positions as occupied
                for (let i = begin; i < end; i++) {
                    occupied.set(i, true);
                }
                decorations.push({ begin, end, match, decorator });
            }
        }
    }

    // Sort by start position
    decorations.sort((a, b) => a.begin - b.begin);

    return decorations;
}

/**
 * Apply decorators to text, yielding text nodes and decorated elements.
 */
function* applyDecorators(
    decorators: CompositeDecorator[],
    textContent: string,
    block: Block<string, string>,
    blocks: Block<string, string>[],
): Generator<Node> {
    const decorations = getDecorations(decorators, textContent);

    let pointer = 0;
    for (const { begin, end, match, decorator } of decorations) {
        // Yield text before decoration
        if (pointer < begin) {
            yield text(textContent.slice(pointer, begin));
        }

        // Yield decorated element
        yield createElement(
            decorator.component,
            { match, block, blocks },
            match[0],
        );

        pointer = end;
    }

    // Yield remaining text
    if (pointer < textContent.length) {
        yield text(textContent.slice(pointer));
    }
}

/**
 * Render decorators, returning a single node.
 */
export function renderDecorators(
    decorators: CompositeDecorator[],
    textContent: string,
    block: Block<string, string>,
    blocks: Block<string, string>[],
): Node {
    const decoratedChildren = Array.from(
        applyDecorators(decorators, textContent, block, blocks),
    );

    if (decoratedChildren.length === 0) {
        return text("");
    }

    if (decoratedChildren.length === 1) {
        return decoratedChildren[0];
    }

    const frag = fragment();
    for (const child of decoratedChildren) {
        appendChild(frag, child);
    }
    return frag;
}

/**
 * Check if decorators should be rendered for this text.
 * Optimization: skip if only BR decorator and no newlines in text.
 */
export function shouldRenderDecorators(
    decorators: CompositeDecorator[],
    textContent: string,
): boolean {
    if (decorators.length === 0) {
        return false;
    }

    // Check if it's just the BR strategy with no newlines
    if (decorators.length === 1) {
        const strategy = decorators[0].strategy;
        const source =
            typeof strategy === "string" ? strategy : strategy.source;
        const isNewlineStrategy = source === "\\n" || source === "\n";
        if (isNewlineStrategy && !textContent.includes("\n")) {
            return false;
        }
    }

    return true;
}
